/**
 * The addresses of the retired paid task board, and where each one goes now.
 *
 * The owner retired paid tasks and AI credits on 2026-10-31: nobody is paid
 * for a task any more, and Blobor, our own game, became a public showcase at
 * `/blobor` (components/blobor/BloborShowcase.tsx). `/tasks` and every
 * address under it are in old messages, on old pages and in agents' notes, so
 * they stay a door rather than a dead end, the way `/projects` did when it
 * retired into `/tasks` on 2026-10-18 (lib/projects.ts):
 *
 * - `/tasks#agent` was the "Use your own agent" section, the only part of the
 *   board a learner still needs, so it lands on the agent page, `/agent`
 *   (lib/agent-keys.ts). `#ai-key` was the AI key beside it, which existed to
 *   connect a coding tool, and lands there too.
 * - Everything else, a task's own page included, lands on the showcase: the
 *   game those tasks were about.
 *
 * main.tsx reads this before anything else reads the address, and rewrites
 * it with `history.replaceState`, so the page that mounts is the new one and
 * Back does not return to an address that would only redirect again.
 */
import { AGENT_PATH } from './agent-keys';
import { isProjectsPath } from './projects';

/** Where the Blobor showcase lives. */
export const BLOBOR_PATH = '/blobor';

/** Whether this address is the showcase; a trailing slash is the same page. */
export function isBloborPath(pathname: string): boolean {
  return /^\/blobor\/?$/.test(pathname);
}

/** Is this the retired task board, or one of its tasks? A prefix is not a path segment. */
export function isTasksPath(pathname: string): boolean {
  return /^\/tasks(\/.*)?$/.test(pathname);
}

/** The board's sections that were about connecting the learner's own agent. */
const AGENT_HASHES: readonly string[] = ['#agent', '#ai-key'];

/**
 * Where a retired address goes, or null when this address is not one.
 *
 * Pure, so it is tested (web/test/retired-tasks.test.ts). The query string is
 * kept, since a campaign tag in it still describes this visit; the hash is
 * dropped, because no section of the new page has the old one's name.
 * `/projects` goes straight to the showcase rather than through `/tasks`.
 */
export function retiredTasksTarget(pathname: string, search: string, hash: string): string | null {
  if (!isTasksPath(pathname) && !isProjectsPath(pathname)) return null;
  const to = isTasksPath(pathname) && AGENT_HASHES.includes(hash) ? AGENT_PATH : BLOBOR_PATH;
  return `${to}${search}`;
}
